<script setup>
const props = defineProps({
  min: Number,
  max: Number,
  from: Number,
  to: Number,
})
const emit = defineEmits(['change'])

const localFrom = ref(props.from)
const localTo = ref(props.to)

const editingFrom = ref(false)
const editingTo = ref(false)

watch(() => props.from, v => { if (!editingFrom.value) localFrom.value = v })
watch(() => props.to, v => { if (!editingTo.value) localTo.value = v })

const toNumber = (value, fallback) => {
  const n = typeof value === 'number' ? value : parseFloat(value)
  return Number.isFinite(n) ? n : fallback
}

const clampFrom = () => {
  const from = toNumber(localFrom.value, props.min)
  const to = toNumber(localTo.value, props.max)
  localFrom.value = Math.max(props.min, Math.min(from, to))
}

const clampTo = () => {
  const from = toNumber(localFrom.value, props.min)
  const to = toNumber(localTo.value, props.max)
  localTo.value = Math.min(props.max, Math.max(to, from))
}

const emitChange = () => {
  clampFrom()
  clampTo()
  emit('change', { from: localFrom.value, to: localTo.value })
}

let debounceTimer = null
const onInputDebounced = (event) => {
  clearTimeout(debounceTimer)
  if (event.inputType?.startsWith('delete')) return
  debounceTimer = setTimeout(() => {
    const fromValid = localFrom.value !== '' && localFrom.value != null && !isNaN(localFrom.value)
    const toValid = localTo.value !== '' && localTo.value != null && !isNaN(localTo.value)
    if (!fromValid || !toValid) return
    emitChange()
  }, 2500)
}

const handleBlurFrom = (event) => {
  clearTimeout(debounceTimer)
  const target = event.target
  setTimeout(() => {
    if (document.activeElement !== target) {
      editingFrom.value = false
      clampFrom()
      emitChange()
    }
  }, 50)
}

const handleBlurTo = (event) => {
  clearTimeout(debounceTimer)
  const target = event.target
  setTimeout(() => {
    if (document.activeElement !== target) {
      editingTo.value = false
      clampTo()
      emitChange()
    }
  }, 50)
}

const fillLeft = computed(() => ((localFrom.value - props.min) / (props.max - props.min) * 100) + '%')
const fillWidth = computed(() => ((localTo.value - localFrom.value) / (props.max - props.min) * 100) + '%')
</script>

<template>
  <div class="range-filter no-flex g-12">
    <div class="range-inputs row g-8">
      <div class="range-input-group no-flex g-4">
        <input class="range-input" type="number" v-model.number="localFrom" :min="min" :max="localTo" @focus="editingFrom = true" @blur="handleBlurFrom($event)" @input="onInputDebounced($event)"/>
      </div>
      <div class="range-separator">—</div>
      <div class="range-input-group no-flex g-4">
        <input class="range-input" type="number" v-model.number="localTo" :min="localFrom" :max="max" @focus="editingTo = true" @blur="handleBlurTo($event)" @input="onInputDebounced($event)"/>
      </div>
    </div>  
    <div class="range-slider-wrap">
      <input class="range-slider range-slider--from" type="range" v-model.number="localFrom" :min="min" :max="max"
             @input="localFrom = Math.min(localFrom, localTo)"
             @change="emitChange()"/>
      <input class="range-slider range-slider--to" type="range" v-model.number="localTo" :min="min" :max="max"
             @input="localTo = Math.max(localTo, localFrom)"
             @change="emitChange()"/>
      <div class="range-track">
        <div class="range-track-fill" :style="{ left: fillLeft, width: fillWidth }"/>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.range-filter {
  padding: 4px 10px 10px;
  width: 100%;
  gap: 30px!important;
}

.range-inputs {
  align-items: center;
}

.range-separator {
  color: $light-black;
}

.range-input {
  width: 80px;
  border: 1px solid $border-grey;
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 13px;
  outline: none;

  &:focus {
    border-color: $black;
  }
}

.range-reset {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  padding: 0 4px;
  color: $light-black;
  transition: color 0.15s;

  &:hover {
    color: $black;
  }
}

.range-slider-wrap {
  position: relative;
  display: flex;
  align-items: center;
  height: 28px;
  width: 100%;
}

.range-slider {
  position: absolute;
  width: 100%;
  height: 100%;
  appearance: none;
  background: transparent;
  z-index: 1000;
  pointer-events: none;
  outline: none;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: $black;
    pointer-events: all;
    cursor: pointer;
  }
}

.range-track {
  position: absolute;
  left: 10px;
  right: 10px;
  height: 4px;
  background: $border-grey;
  border-radius: 2px;
  pointer-events: none;
}

.range-track-fill {
  position: absolute;
  height: 100%;
  background: $black;
  border-radius: 2px;
}
</style>
